import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { AppError, indexBy, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, switchMap } from 'rxjs';

import { Account, CardProduct, Currencies, Currency, GetSkinsRes, WalletsSkinCollection, WalletType } from '@zeusm/business/core/entities';
import { setErrorStatusFor, setPendingStatusFor, setSuccessStatusFor, withRequestHandler, withRequestStatus } from '@zeusm/shared/store';

import { BUSINESS_DICTIONARIES_API } from './api';

const EMPTY_WALLET_SKINS: WalletsSkinCollection = { card: [], crypto: [] };

interface DictionariesState {
  currency: Currency;
  currencies: Currencies;
  user: Account;
  cardProducts: CardProduct[];
  skins: GetSkinsRes;
}

const initialState: DictionariesState = {
  currency: null,
  currencies: null,
  user: null,
  cardProducts: [],
  skins: null,
};

export const BusinessDictionariesStore = signalStore(
  withState(initialState),
  withRequestStatus(['getCurrencies', 'getSkins', 'getCardProducts']),
  withRequestHandler(),
  withProps(store => ({
    store,
    spinnerService: inject(SpinnerService),
    dictionariesApi: inject(BUSINESS_DICTIONARIES_API),
  })),
  withComputed(store => ({
    selectFiatCurrencies: computed(() => store.currencies()?.fiat || []),
    selectCryptoCurrencies: computed(() => store.currencies()?.crypto || []),
  })),
  withComputed(store => ({
    selectAllCurrencies: computed(() => [...store.selectFiatCurrencies(), ...store.selectCryptoCurrencies()]),
    selectIsReadyForGetWallets: computed(() => {
      return (store.isGetCurrenciesSuccess() || store.getCurrenciesError()) && store.isGetSkinsSuccess();
    }),
    cardSkins: computed(() => store.skins()?.cardSkins ?? []),
    walletSkins: computed(() => store.skins()?.walletSkins ?? EMPTY_WALLET_SKINS),
    binsByCardProductId: computed(() => new Map(store.cardProducts().map(cardProduct => [cardProduct.id, cardProduct.binCode]))),
  })),
  withMethods(store => ({
    selectCurrencyByEntity: (currency: string, entity: string) => {
      const currencies = store.selectAllCurrencies();
      const currenciesIndex = indexBy(currencies, entity);
      return currenciesIndex[currency] || null;
    },
    selectWalletSkinsByType: (type: WalletType) => computed(() => store.walletSkins()?.[type]),
    changeCurrency: (currency: Currency) => patchState(store, { currency }),
    setUser: (user: Account) => patchState(store, { user }),
    getCardProductById: (id: string) => store.cardProducts().find(cardProduct => cardProduct.id === id),
    getCardProductByBinCode: (binCode: string) => store.cardProducts().find(cardProduct => cardProduct.binCode === binCode),
    clearOrgScopedData: () => {
      patchState(store, {
        cardProducts: [],
        skins: null,
      });
      store.resetRequestStatus('getCardProducts');
      store.resetRequestStatus('getSkins');
    },
  })),
  withMethods(store => ({
    selectCurrencyByCode: (currency: string) => computed(() => store.selectCurrencyByEntity(currency, 'code')),
    selectCurrencyByName: (currency: string) => computed(() => store.selectCurrencyByEntity(currency, 'name')),
  })),
  withMethods(({ store, dictionariesApi }) => ({
    getCurrencies: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getCurrencies'));
        return (store.currencies() ? of(store.currencies()) : dictionariesApi.getCurrencies()).pipe(
          tapResponse({
            next: currencies => {
              patchState(store, { currencies }, setSuccessStatusFor('getCurrencies'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getCurrencies', error)),
          }),
        );
      }),
    ),
    getSkins: rxMethod<void>(
      switchMap(() => {
        if (store.isGetSkinsSuccess()) {
          return of(store.skins());
        }

        patchState(store, setPendingStatusFor('getSkins'));
        return dictionariesApi.getSkins().pipe(
          tapResponse({
            next: skins => {
              patchState(store, { skins }, setSuccessStatusFor('getSkins'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getSkins', error)),
          }),
        );
      }),
    ),
    getCardProducts: rxMethod<void>(
      switchMap(() => {
        if (store.isGetCardProductsSuccess()) {
          return of(store.cardProducts());
        }

        patchState(store, setPendingStatusFor('getCardProducts'));
        return dictionariesApi.getCardProducts().pipe(
          tapResponse({
            next: cardProducts => patchState(store, { cardProducts }, setSuccessStatusFor('getCardProducts')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getCardProducts', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));
    },
  }),
);
